import { ButtonType, ButtonWidget, CompactInlineAlertWidget, DropdownButtonType, InlineAlertType, List, ListItem, SingleSelectDropdown, TextFieldWidget } from '@tableau/widgets';
import React from 'react';
import { match } from 'react-router-dom';
import _ from 'underscore';

import {Location} from '~commonInfra/utils/Location';
import { AuthenticationTypeResolutionService } from '~src/authentication/services/AuthenticationTypeResolutionService';
import {siteUrlsSelector} from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import {getAppStore} from '~src/bootstrap/redux/store/AppStore';
import { RouterError } from '~src/bootstrap/routerHelpers/RouterError';
import { Messages } from '~src/codegen/Localize';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { CommonErrorCode } from '~src/commonInfra/utils/ErrorCodes';
import { sanitize } from '~src/commonInfra/utils/Sanitize';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { UserSettingsStyle } from '~src/userSettings/styles/UserSettingsStyle';

import { OIDCAuthenticationProvider, OIDCProviderProps } from '../directives/OIDCAuthenticationProvider';
import { SAMLAuthenticationProvider, SAMLProviderProps } from '../directives/SAMLAuthenticationProvider';
import { AuthenticationProviderType, IAuthenticationProvider, IOIDCAuthenticationProvider, ISAMLAuthenticationProvider, ISiteAuthenticationSettings, SiteAuthenticationSettingsService } from '../services/SiteAuthenticationSettingsService';
import { dropDownWidgetDescriptionText, dropDownWidgetListItem, dropDownWidgetTitleText } from '../styles/ExternalAuthenticationProviderStyle';
import { samlProviderAlertText, samlProviderGroupContent, samlProviderGroupNumber, samlProviderGroupSettings, samlProviderMatchAttributesTitleStyle, samlProviderTextDescriptionStyle, samlProviderTextTitleStyle } from '../styles/SAMLAuthenticationProviderStyle';
import { backButtonSectionStyle, newConfigurationContainer,newConfigurationHeaderText, textFieldContainer } from '../styles/SiteAuthenticationSettingsStyles';
import { AuthenticationBackButton } from './AuthenticationBackButton';
import { DiscardAuthenticationChangesDialog } from './multipleIDPComponents/DiscardAuthenticationChangesDialog';


export interface AuthenticationConfigurationSpaceComponentProps {
  isNewConfigPage?: boolean;
}

export interface AuthenticationConfigurationSpaceMatchProps {
  config?: string;
}

export type AuthenticationConfigurationRoutedProps = match<AuthenticationConfigurationSpaceMatchProps>;

export type AuthenticationConfigurationSpaceProps = AuthenticationConfigurationSpaceComponentProps & AuthenticationConfigurationRoutedProps;

export interface AuthenticationConfigurationSpaceState {
  siteAuthenticationSettings: ISiteAuthenticationSettings;
  // isConnectedClientsVisible: boolean;
  // isEmbeddedVizForSiteSamlSupported: boolean;
  // isExternalProviderEnabledChanged: () => void;
  // isTableauIdSupported: boolean;
  // usersUrl: string;
  getProviderDisplayName: (provider: IAuthenticationProvider) => string;
  getProviderDescription: (provider: IAuthenticationProvider) => string;
  // download: (evt: Event, url: string) => void;
  // getHelp: (category?: string) => void;
  // aboutToBeDisabled: IAuthenticationProvider[];
  // aboutToLockMyselfOut: boolean;
  // defaultAuthEmbeddedViewsButtons: RadioButtonWithLabelOption<string>[];
  // authenticationTypesProviderButtons: RadioButtonWithLabelOption<string>[];
  authenticationTypesDropDownListItems: ListItem[];
  selectedProvider: IAuthenticationProvider;
  userAttributes: IUserAttributes;
  // defaultAuthEmbeddedViewsRadioButtonIdx: number;
  // authenticationTypesRadioButtonIdx: number;
  samlProvider: ISAMLAuthenticationProvider;
  samlProviderProps: SAMLProviderProps;
  samlMetadataFile: File;
  isSamlMetadataFileChanged: boolean;
  oidcProvider: IOIDCAuthenticationProvider;
  oidcProviderProps: OIDCProviderProps;
  savedSalesforceDomain: string;
  configurationIsSavedToDatabase: boolean;
  hasError: boolean;
  allProviders: IAuthenticationProvider[];
  configurationIdForNewlySavedProvider: string;
  idpConfigurationName: string;
}

const SALESFORCE_DOMAIN_PLACEHOLDER = 'login.salesforce.com';
const IDP_CONFIGURATION_NAME_REGEXP: RegExp = new RegExp('^([a-zA-Z0-9 _-]+)$');
const DEFAULT_IDP_CONFIG_ID_OIDC = '00000000-0000-0000-0000-000000000000';
const DEFAULT_IDP_CONFIG_ID_SAML = '11111111-1111-1111-1111-111111111111';
const IDP_CONFIG_NAME_LENGTH = 35;

interface IUserAttributes extends _.Dictionary<string> {}


export class AuthenticationConfigurationSpace extends React.Component<AuthenticationConfigurationSpaceProps, AuthenticationConfigurationSpaceState> {
  private messageListener: (evt: MessageEvent) => void;

  constructor(props: AuthenticationConfigurationSpaceProps) {
    super(props);
    this.state = {
      siteAuthenticationSettings: undefined,
      getProviderDisplayName: (provider: IAuthenticationProvider) => {
        return provider
          ? SiteAuthenticationSettingsService.getProviderDisplayName(provider)
          : Messages.siteAuthenticationTypeSSO();
      },
      getProviderDescription: (provider: IAuthenticationProvider) => {
        if ((provider as IOIDCAuthenticationProvider)?.alias === 'OpenId Connect') {
          return Messages.ssoAuthTypeOIDC_description_OpenID_Connect2();
        }
        if ((provider as IOIDCAuthenticationProvider)?.alias === 'Google') {
            return Messages.ssoAuthTypeGoogle_description();
        }
        switch (provider.type) {
          case AuthenticationProviderType.oidc:
            return Messages.ssoAuthTypeOIDC_description();
          case AuthenticationProviderType.saml:
            return Messages.ssoAuthTypeSAML_description_2();
          case AuthenticationProviderType.default:
            return Messages.ssoAuthTypeDefault_description();
        }
      },
      authenticationTypesDropDownListItems: [],
      selectedProvider: undefined,
      userAttributes: null,
      samlProvider: undefined,
      samlProviderProps: {
        provider: undefined
      },
      samlMetadataFile: null,
      isSamlMetadataFileChanged: false,
      oidcProvider: undefined,
      oidcProviderProps: {
        provider: undefined,
        showTestConfiguration: false,
        hasUpdated: false
      },
      savedSalesforceDomain: undefined,
      configurationIsSavedToDatabase: !props.isNewConfigPage,
      hasError: false,
      allProviders: [],
      configurationIdForNewlySavedProvider: '',
      idpConfigurationName: '',
    };
    this.messageListener = (evt: MessageEvent) => this.receiveMessage(evt);
  }

  public componentDidMount() {
    this.setupInitialState(false);
  }

  private setupInitialState(refresh: boolean = false) {
    SiteAuthenticationSettingsService.getSiteAuthenticationSettings(refresh).then((siteAuthSettings: ISiteAuthenticationSettings) => {
      this.setState({ siteAuthenticationSettings: siteAuthSettings });

      this.setState({ authenticationTypesDropDownListItems: this.getAuthenticationTypesProviderList() });

      let samlProvider;
      // set provider variables for a new page
      if (this.props.isNewConfigPage && !this.state.configurationIsSavedToDatabase) {
        // set external providers to blank template
        let siteAuthenticationSettings = siteAuthSettings;
        this.setSiteAuthSettingsToBlankTemplate(siteAuthenticationSettings);
        this.setState({ siteAuthenticationSettings: siteAuthenticationSettings });

        samlProvider = _.find(siteAuthSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.type === 'SAML')) as ISAMLAuthenticationProvider;
        const salesForceProvider = _.find(siteAuthSettings.externalProviders, (provider: IAuthenticationProvider) => (provider as IOIDCAuthenticationProvider).alias === 'Salesforce') as IOIDCAuthenticationProvider;
        this.setState({savedSalesforceDomain: salesForceProvider?.salesforceDomain});

        // Set oidcProvider to defined value
        let oidcProvider = _.find(siteAuthSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === 'OIDC')) as IOIDCAuthenticationProvider;
        this.setState({ oidcProvider: oidcProvider as IOIDCAuthenticationProvider });
      }

      // set variables for a newly saved configuration
      if (this.props.isNewConfigPage && this.state.configurationIsSavedToDatabase) {
        samlProvider = _.find(siteAuthSettings.allProviders, (provider: IAuthenticationProvider) => (provider.type === 'SAML' && provider?.idpConfigurationName === this.state.idpConfigurationName))as ISAMLAuthenticationProvider;
      }
      // set provider variables if this is an edit page
      const configurationId = this.props.params.config; // ? this.props.params.config : 'whatever the new idpConfigurationId is.  how do we get this?'
      if (!this.props.isNewConfigPage) {
        if (configurationId) {
          const provider = _.find(siteAuthSettings.allProviders, (provider: IAuthenticationProvider) => (provider.idpConfigurationId === configurationId));
          if (provider && provider.type === 'OIDC') {
            this.setState({ selectedProvider: provider});
            this.setState({ oidcProvider: provider as IOIDCAuthenticationProvider });
            this.setState({savedSalesforceDomain: (provider as IOIDCAuthenticationProvider)?.salesforceDomain});
            this.setState({ idpConfigurationName: provider.idpConfigurationName });
          } else if (provider && provider.type === 'SAML') {
            this.setState({ selectedProvider: provider});
            samlProvider = _.find(siteAuthSettings.allProviders, (provider: IAuthenticationProvider) => (provider.idpConfigurationId === configurationId)) as ISAMLAuthenticationProvider;
            this.setState({ idpConfigurationName: provider.idpConfigurationName });
          } else {
            this.setState({ hasError: true });
          }
        }
      }
      this.setState({ samlProvider: {
          ...samlProvider,
          attributeMapping: { ...samlProvider?.attributeMapping }
        }});
      this.setState({
        samlProviderProps: {
          provider: this.state.samlProvider,
          updateSamlProvider: this.updateSamlProvider,
          updateEmbeddedOption: this.updateEmbeddedOption,
          updateAllowFullName: this.updateAllowFullName,
          updateFirstNameMapping: this.updateFirstNameMapping,
          updateLastNameMapping: this.updateLastNameMapping,
          updateFullNameMapping: this.updateFullNameMapping,
          updateEmailMapping: this.updateEmailMapping,
          updateSAMLMetadataFile: this.updateSAMLMetadataFile
        }
      });
      this.setState({
        oidcProviderProps: {
          provider: this.state.oidcProvider,
          // todo: this needs to be changed to if this is saved
          showTestConfiguration: this.state.oidcProvider == null ? false : this.state.oidcProvider.isEnabled,
          hasUpdated: false,
          updateClientId: this.updateOIDCClientId,
          updateClientSecret: this.updateOIDCClientSecret,
          updateConfigurationUrl: this.updateOIDCConfigurationUrl,
          updateEmbeddedOption: this.updateOIDCEmbeddedOption,
          updateUseFullName: this.updateOIDCUseFullName,
          updateFirstNameMapping: this.updateOIDCFirstNameMapping,
          updateLastNameMapping: this.updateOIDCLastNameMapping,
          updateFullNameMapping: this.updateOIDCFullNameMapping,
          updateEmailMapping: this.updateOIDCEmailMapping,
          updateSingleLougout: this.updateOIDCSingleLougout
        }
      });
      this.setState({isSamlMetadataFileChanged: false});
      this.setupMessageListener();
      this.setState({ allProviders: siteAuthSettings.allProviders });
    });
  }

  private setSiteAuthSettingsToBlankTemplate(siteAuthSettings: ISiteAuthenticationSettings) {
    let samlProvider = _.find(siteAuthSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.type === 'SAML')) as ISAMLAuthenticationProvider;
    samlProvider.idpConfigurationId = null;
    samlProvider.attributeMapping = {
      emailMapping: 'NameID',
      firstNameMapping: 'FirstName',
      lastNameMapping: 'LastName',
      fullNameMapping: 'FullName',
      useFullName: false
    };
    samlProvider.identityProviderEntityId = '';
    samlProvider.ssoServiceURL = '';
    samlProvider.attributeMapping.useFullName = false;
    samlProvider.allowEmbeddedAuthentication = false;

    let oidcProvider = _.find(siteAuthSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === 'OIDC')) as IOIDCAuthenticationProvider;
    oidcProvider.clientId = '';
    oidcProvider.clientSecret = '';
    oidcProvider.configurationUrl = '';
    oidcProvider.useFullName = false;
    oidcProvider.emailMapping = '';
    oidcProvider.firstNameMapping = '';
    oidcProvider.lastNameMapping = '';
    oidcProvider.fullNameMapping = '';
    oidcProvider.idpConfigurationId = '';
    oidcProvider.allowEmbeddedAuthentication = false;
    oidcProvider.isLogoutSupported = false;
  }

  private setIdpConfigurationName(name: string) {
    // set IdPConfigurationName for selectedProvider?  Set it for the provider variable that we want to send via api. Does it matter whether this is OIDC or
    this.setState({idpConfigurationName: name.trim()});
  }

  private getAuthenticationTypesProviderList(): ListItem[] {
    const providerItems: ListItem[] = [];

    const SALESFORCE_DOMAIN_PLACEHOLDER = 'login.salesforce.com';

    this.state.siteAuthenticationSettings?.externalProviders.forEach((provider) => {
      const externalProviderButtonDetail =
        provider.type === AuthenticationProviderType.oidc && (provider as IOIDCAuthenticationProvider).alias === 'Salesforce'
          ? <div style={dropDownWidgetDescriptionText}><div>{Messages.ssoAuthTypeOIDC_description_salesforce({ DOMAIN: (provider as IOIDCAuthenticationProvider)?.salesforceDomain || SALESFORCE_DOMAIN_PLACEHOLDER })}</div></div>
          : <div style={dropDownWidgetDescriptionText} dangerouslySetInnerHTML={{ __html: sanitize(this.state.getProviderDescription(provider)) }} />;

      providerItems.push(
        {
          content:
            <div style={dropDownWidgetListItem}>
              <div style={dropDownWidgetTitleText}>
                {this.state.getProviderDisplayName(provider)}
              </div>
              {externalProviderButtonDetail}
            </div>,
          testId: 'authentication-type-sso-button',
          value: provider.id
        }
      );
    });
    return providerItems;
  }

  // should only be used when we are saving a brand new configuration
  changeProvider(id: string) {
    let changeToProvider = _.find(this.state.siteAuthenticationSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === id));
    // if value is oidc, then we need to set selectedProvider to a blank value
    if (changeToProvider.type === AuthenticationProviderType.oidc) {
      let newOidcProvider = { ...changeToProvider, clientId: '', clientSecret: '', configurationUrl: '', idpConfigurationId: ''} as IOIDCAuthenticationProvider;
      this.setState({ selectedProvider: newOidcProvider }, () => {
          this.setState({ oidcProvider: newOidcProvider});
          const oidcProviderProps = this.state.oidcProviderProps;
          oidcProviderProps.provider = newOidcProvider;
          this.setState({ oidcProviderProps: oidcProviderProps });
      });
    } else {
      this.setState({ selectedProvider: changeToProvider });
    }
  }

  // SAML PROVIDER FUNCTIONS

  private updateSamlProvider = () => {
    return;
  };

  private updateEmbeddedOption = (allow: boolean) => {
    let samlProvider = this.state.samlProvider;
    samlProvider.allowEmbeddedAuthentication = allow;
    this.setState({ samlProvider: samlProvider });
  };

  private allFieldsAreFilledOut(): boolean {
    return (this.state.idpConfigurationName && (this.state.samlProvider?.identityProviderEntityId && !!this.state.samlMetadataFile) &&
      !!this.state.samlProvider?.attributeMapping?.emailMapping &&
      (this.state.samlProvider?.attributeMapping?.useFullName ? !!this.state.samlProvider?.attributeMapping?.fullNameMapping
        : (!!this.state.samlProvider?.attributeMapping?.firstNameMapping && !!this.state.samlProvider?.attributeMapping?.lastNameMapping)));
  }

  private changesArePresent(): boolean {
    // check if samlProvider is different (only checks ssoServiceURL and idp EntityID)

    // completely new config page always has changes present
    if (this.props.isNewConfigPage && !this.state.configurationIsSavedToDatabase) return true;

    // edit config page checks all providers
    const internalProvider = _.find(this.state.siteAuthenticationSettings?.allProviders, (provider: IAuthenticationProvider) => (provider.type === 'SAML' && provider.idpConfigurationId === this.props.params?.config)) as ISAMLAuthenticationProvider;
    return !_.isEqual(internalProvider, this.state.samlProvider) || this.state.isSamlMetadataFileChanged || this.isIdpConfigurationNameChanged();
  }

  private isConnectionUsable(): boolean {
    return !!(_.find(this.state.siteAuthenticationSettings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === 'SAML')) as ISAMLAuthenticationProvider).identityProviderEntityId;
  }

  private updateAllowFullName = (allow: boolean) => {
    let samlProvider = this.state.samlProvider;
    samlProvider.attributeMapping.useFullName = allow;
    this.setState({ samlProvider: samlProvider });
  };

  private updateFirstNameMapping = (name: string) => {
    let samlProvider = this.state.samlProvider;
    samlProvider.attributeMapping.firstNameMapping = name;
    this.setState({ samlProvider: samlProvider });
  };

  private updateLastNameMapping = (name: string) => {
    let samlProvider = this.state.samlProvider;
    samlProvider.attributeMapping.lastNameMapping = name;
    this.setState({ samlProvider: samlProvider });
  };

  private updateFullNameMapping = (name: string) => {
    let samlProvider = this.state.samlProvider;
    samlProvider.attributeMapping.fullNameMapping = name;
    this.setState({ samlProvider: samlProvider });
  };

  private updateEmailMapping = (email: string) => {
    let samlProvider = this.state.samlProvider;
    samlProvider.attributeMapping.emailMapping = email;
    this.setState({ samlProvider: samlProvider });
  };

  private updateSAMLMetadataFile = (file: File, identityProviderEntityId: string, ssoServiceURL: string) => {
    let samlProvider = this.state.samlProvider;
    samlProvider.identityProviderEntityId = identityProviderEntityId;
    samlProvider.ssoServiceURL = ssoServiceURL;
    this.setState({ samlProvider: samlProvider, samlMetadataFile: file, isSamlMetadataFileChanged: true });
  };

  private saveSAMLAuthenticationProviderChanges() {
    this.sendSaveRequests().then(() => {
      ToasterService.instance.pop({ type: ToastType.Success, text: Messages.siteAuthenticationSetToSAML() });
      ToasterService.instance.pop({ type: ToastType.Info, text: Messages.siteAuthenticationTestConnectionDesc2()});
      this.setState({ configurationIsSavedToDatabase: true });
      this.setupInitialState(true);
    });
  }

  private handleUpdate(promise: Promise<any>): Promise<any> {
    promise.catch((reason: number) => {
      let translateKey = Messages.unexpectedError();
      if (reason === CommonErrorCode.InsecureSAMLEndpoint) {
        translateKey = Messages.siteAuthenticationIdPImportFailureInsecureEndpoint(this.translateValues);
      } else if (reason === CommonErrorCode.InvalidSAMLIdpMetadata) {
        translateKey = Messages.siteAuthenticationIdPImportFailureInvalidMetadata();
      }
      ToasterService.instance.pop({ type: ToastType.Error, text: translateKey});
    });
    return promise;
  }

  private sendSaveRequests(): Promise<void> {
    let samlProvider = this.state.samlProvider;
    samlProvider.idpConfigurationName = this.state.idpConfigurationName;
    return this.handleUpdate(SiteAuthenticationSettingsService.createOrUpdateSAMLConfiguration(samlProvider, this.state.samlMetadataFile));
  }

  // OIDC Functions

  private isSelectedProviderTypeOIDC(): boolean {
    return this.state.selectedProvider?.type === AuthenticationProviderType.oidc;
  }

  private isGenericOIDCSelected(): boolean {
    return this.state.selectedProvider?.type === AuthenticationProviderType.oidc
      && (((this.state.selectedProvider as IOIDCAuthenticationProvider)?.alias === 'OpenId Connect' || !(this.state.selectedProvider as IOIDCAuthenticationProvider)?.alias)
        // For editing OIDC page, selectedProvider must be defined with a null alias.  If we only check for null alias,
        // we might unintentionally render oidc component in the initial render
        && (!!this.state.oidcProviderProps?.provider));
  }

  private updateOIDCClientId = (clientId: string) => {
    let oidcProvider = this.state.oidcProvider;
    oidcProvider.clientId = clientId;
    this.setState({ oidcProvider: oidcProvider});
    this.oidcHasUpdated();
  };

  private updateOIDCClientSecret = (clientSecret: string) => {
    let oidcProvider = this.state.oidcProvider;
    oidcProvider.clientSecret = clientSecret;
    this.setState({ oidcProvider: oidcProvider});
    this.oidcHasUpdated();
  };

  private updateOIDCConfigurationUrl = (url: string) => {
    let oidcProvider = this.state.oidcProvider;
    oidcProvider.configurationUrl = url;
    this.setState({ oidcProvider: oidcProvider});
    this.oidcHasUpdated();
  };

  private updateOIDCEmailMapping = (claim: string) => {
    let oidcProvider = this.state.oidcProvider;
    oidcProvider.emailMapping = claim;
    this.setState({ oidcProvider: oidcProvider});
    this.oidcHasUpdated();
  };

  private updateOIDCUseFullName = (useFullName: boolean) => {
    let oidcProvider = this.state.oidcProvider;
    oidcProvider.useFullName = useFullName;
    this.setState({ oidcProvider: oidcProvider});
    this.oidcHasUpdated();
  };

  private updateOIDCFullNameMapping = (claim: string) => {
    let oidcProvider = this.state.oidcProvider;
    oidcProvider.fullNameMapping = claim;
    this.setState({ oidcProvider: oidcProvider});
    this.oidcHasUpdated();
  };

  private updateOIDCFirstNameMapping = (claim: string) => {
    let oidcProvider = this.state.oidcProvider;
    oidcProvider.firstNameMapping = claim;
    this.setState({ oidcProvider: oidcProvider});
    this.oidcHasUpdated();
  };

  private updateOIDCLastNameMapping = (claim: string) => {
    let oidcProvider = this.state.oidcProvider;
    oidcProvider.lastNameMapping = claim;
    this.setState({ oidcProvider: oidcProvider});
    this.oidcHasUpdated();
  };

  private updateOIDCSingleLougout = (enable: boolean) => {
    let oidcProvider = this.state.oidcProvider;
    oidcProvider.isLogoutSupported = enable;
    this.setState({ oidcProvider: oidcProvider });
    this.oidcHasUpdated();
  };

  private updateOIDCEmbeddedOption = (allow: boolean) => {
    let oidcProvider = this.state.oidcProvider;
    oidcProvider.allowEmbeddedAuthentication = allow;
    this.setState({ oidcProvider: oidcProvider });
    this.oidcHasUpdated();
  };

  // This should be called if generic OIDC config has been edited.
  private oidcHasUpdated() {
    let oidcProviderProps = this.state.oidcProviderProps;
    oidcProviderProps.hasUpdated = true;
    this.setState({ oidcProviderProps: oidcProviderProps });
  }

  private updateOIDCSalesforceDomain = (domain: string) => {
    let oidcProvider = this.state.oidcProvider;
    oidcProvider.salesforceDomain = domain;
    this.setState({ oidcProvider: oidcProvider });
  };

  private canSaveOIDCProvider(): boolean {
    const oidcProvider = this.state.oidcProvider;
    if (oidcProvider == null) {
      return false;
    }
    if (oidcProvider.type !== 'OIDC') {
      return true;
    }
    if (!this.state.oidcProviderProps.hasUpdated) {
      return false;
    }
    if (oidcProvider.clientId != null && oidcProvider.clientId.length !== 0 &&
      oidcProvider.clientSecret != null && oidcProvider.clientSecret.length !== 0 &&
      oidcProvider.configurationUrl != null && oidcProvider.configurationUrl.length !== 0) {
      return true;
    }
  }

  private saveChangesOnClickOIDC = () => {
    let oidcProvider = this.state.oidcProvider;
    oidcProvider.isEnabled = true;
    oidcProvider.idpConfigurationName = this.state.idpConfigurationName;
    SiteAuthenticationSettingsService.saveOIDCProvider(oidcProvider).then((result) => {
      if (result.errors && result.errors.length) {
        throw new Error();
      }

      const provider = oidcProvider.type === 'OIDC' ? oidcProvider.id : oidcProvider.alias;
      if (this.isGenericOIDCSelected()) {
        ToasterService.instance.pop({ type: ToastType.Info, text: Messages.siteOIDCConfigurationToastForTest() });
      }
      ToasterService.instance.pop({ type: ToastType.Success, text: Messages.siteOIDCConfigurationToastForSave({PROVIDER: provider}) });

      const oidcProviderProps = this.state.oidcProviderProps;
      oidcProviderProps.showTestConfiguration = true;
      oidcProviderProps.hasUpdated = false;
      this.setState({ oidcProviderProps: oidcProviderProps });
      this.setState({ configurationIsSavedToDatabase: true });
    }).then(() => {
      if (oidcProvider.alias === 'Google' || oidcProvider.alias === 'Salesforce') {
        const urls = siteUrlsSelector(getAppStore().getState());
        const authSettingsUrl = urls.settingsAuthenticationUrl();
        Location.instance.setUrl(authSettingsUrl);
      } else {
        this.setupInitialState(true);
      }
    }).catch(() => {
      ToasterService.instance.pop({ type: ToastType.Error, text: Messages.unexpectedError() });
    });
  };

  // Cancel and Save Logic

  private cancelOnClick = () => {
      DiscardAuthenticationChangesDialog();
  };

  private saveChangesOnClick = () => {
    if (this.isSelectedProviderTypeOIDC()) {
      this.saveChangesOnClickOIDC();
    } else if (this.state.selectedProvider?.type === 'SAML') {
      this.saveSAMLAuthenticationProviderChanges();
    }
  };

  private isSaveDisabled(): boolean {
    //  Save should be enabled when a configuration (1) is completely filled out. (2) has any new changes
    //
    //  Save should be disabled when a configuration (1) is not filled out (2) doesn't have any new changes
    if (this.state.idpConfigurationName.trim().length === 0) {
      return true;
    }

    if (!this.state.configurationIsSavedToDatabase && this.isIdpConfigurationNameDuplicate(this.state.idpConfigurationName)) {
      return true;
    }

    if (!this.state.idpConfigurationName
      || !this.isValidIdpConfigurationName(this.state.idpConfigurationName)
      || this.isIdpConfigurationNameExceedingMaxLength(this.state.idpConfigurationName)) {
      return true;
    }
    if (this.isGenericOIDCSelected()) {
      return !this.canSaveOIDCProvider();
    } else if (this.state.selectedProvider?.type === 'SAML') {
      return !(this.allFieldsAreFilledOut() && (this.changesArePresent()));
    }
    if (this.state.oidcProvider.alias === 'Google') {
      return !this.isIdpConfigurationNameChanged();
    }
    if (this.state.oidcProvider.alias === 'Salesforce') {
      return !this.state.oidcProvider.salesforceDomain || !this.isIdpConfigurationNameChanged();
    }
    return !this.state.selectedProvider;
  }

  private isCancelDisabled(): boolean {
    if (this.state.idpConfigurationName) {
      return false;
    }
    //  Cancel should be enabled when a configuration (1) is new (2) has any unsaved changes
    //
    //  Cancel should be disabled when a configuration (1) is not filled out (2) doesn't have any new changes
    if (this.isGenericOIDCSelected()) {
      return !this.state.oidcProviderProps.hasUpdated;
    } else if (this.state.selectedProvider?.type === 'SAML') {
      return !(this.changesArePresent());
    } else if ((this.state.selectedProvider as IOIDCAuthenticationProvider)?.alias === 'Salesforce') {
      return (this.state.oidcProvider?.salesforceDomain === this.state.savedSalesforceDomain);
    }
    return this.state.configurationIsSavedToDatabase;
  }

  // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
  private test() {
    WindowLocationService.open(this.state.selectedProvider.testLoginUrl,
      'testLogin', { left: 20, top: 20, width: 500, height: 600, toolbar: 1, resizable: 1 });
  }

  // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
  private downloadUrl(evt: React.MouseEvent<HTMLButtonElement>, url: string) {
    evt.preventDefault();
    WindowLocationService.download(url);
  }

  // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
  private downloadLog(evt: React.MouseEvent<HTMLButtonElement>) {
    this.downloadUrl(evt, WebHttpClient.FILE_UPLOAD_API_PREFIX + 'siteSAMLLog');
  }

  // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
  private translateValues = {
    SERVER_LEVEL_AUTH: AuthenticationTypeResolutionService.siteAuthDefaultLabel(),
    SERVER_NAME: ServerSettingsUnauthenticated.serverName()
  };

  // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
  private setUserAttributes(userAttributes: IUserAttributes) {
    this.setState({userAttributes: userAttributes});
  }

  // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
  private getHostName(url: string): string {
    // Can't use URL since it's not supported in IE (or node)
    const urlParsingNode = document.createElement('a');
    urlParsingNode.setAttribute('href', url);
    return urlParsingNode.hostname;
  }

  // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
  private getBaseDomain(url: string): string {
    const hostName = this.getHostName(url);
    // If the host name is ip address, returns the ip address as the domain.
    if (this.isIPV4(hostName)) {
      return hostName;
    }

    const splitHost = hostName.split('.');
    const tldregex = new RegExp('(co|com|gov|gouv|net|org|edu|mil)');
    if (splitHost.length>=3 && tldregex.test(splitHost[splitHost.length-2])) {
      return splitHost.slice(-3).join('.');
    }
    else {
      return splitHost.slice(-2).join('.');
    }
  }

  // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
  private isIPV4(host: string): boolean {
    const elements = host.split('.');
    if (elements.length === 4) {
      return elements.every((element) => {
        const value = parseInt(element, 10);
        return !isNaN(value) && value >=0 && value <= 255;
      });
    }

    return false;
  }

  // TEST CONNECTION FUNCTIONALITY

  // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
  private isRunningUnderTest(): boolean {
    return window.hasOwnProperty('mocha');
  }

  // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
  public receiveMessage(evt: MessageEvent) {
    if (this.getHostName(evt.origin) !== this.getHostName(this.state.selectedProvider?.testLoginUrl) && !this.isRunningUnderTest()) {
      return;
    }
    const userAttributes: IUserAttributes = JSON.parse(evt.data);
    this.setUserAttributes(userAttributes);
    if (!this.isRunningUnderTest()) {
      // (TFS 700140) This line causes the Karma debug window to close, when run via the unit tests
      (evt.source as any).close();
    }
  }

  // copied from SAMLAuthenticationProvider for SAML's TestConnection Button
  private setupMessageListener() {
    const baseDomain = this.getBaseDomain(WindowLocationService.getOrigin());
    const testLoginBaseDomain = this.getBaseDomain(this.state.selectedProvider?.testLoginUrl);

    // Enable cross-origin access to this window from the new window, provided they share the same base domain
    if (baseDomain === testLoginBaseDomain) {
      // Test login window is expected to postMessage bearing resolved UserDetails
      window.addEventListener('message', this.messageListener);
      if (!this.isRunningUnderTest()) {
        document.domain = baseDomain;
      }
    }
  }

  private isValidIdpConfigurationName(idpConfigurationName: string): boolean {
    return idpConfigurationName === '' || IDP_CONFIGURATION_NAME_REGEXP.test(idpConfigurationName);
  }

  private isIdpConfigurationNameDuplicate(idpConfigurationName: string): boolean {
    return idpConfigurationName !== '' && !!_.find(this.state.allProviders, (provider: IAuthenticationProvider) => (provider.idpConfigurationName.toLowerCase() === idpConfigurationName.toLowerCase()));
  }

  private isIdpConfigurationNameExceedingMaxLength(idpConfigurationName: string): boolean {
    return idpConfigurationName !== '' && idpConfigurationName.length > IDP_CONFIG_NAME_LENGTH;
  }

  private isIdpConfigurationNameChanged() {
    return this.state.configurationIsSavedToDatabase ? this.state.idpConfigurationName !== this.state.selectedProvider?.idpConfigurationName : true;
  }

  private renderAuthenticationTypes(): JSX.Element {
    const isGenericOIDC = this.isGenericOIDCSelected();
    const isSAMLSelected = this.state.selectedProvider?.type === 'SAML';
    return <>
      <div>
        <span style={backButtonSectionStyle}>
            <AuthenticationBackButton/>
        </span>
        <div style={newConfigurationHeaderText}>{this.props.isNewConfigPage ? Messages.siteAuthenticationNewLoginConfiguration() : Messages.siteAuthenticationEditLoginConfiguration()}</div>
        <div style={{maxWidth: 400}} className="tb-settings-msg">
          {Messages.siteAuthenticationNewLoginConfigurationDesc()}
        </div>
        <div>
          {this.props.isNewConfigPage ?
            <div style={{width: 390}}>
              <SingleSelectDropdown
                list={<List items={this.state.authenticationTypesDropDownListItems} style={{maxWidth: 390}}/>}
                onChange={this.changeProvider.bind(this)}
                value={this.state.selectedProvider?.id}
                label='Authentication'
                type={DropdownButtonType.Outline}
                style={{ marginBottom: CommonStyles.Sizes.MediumSpace }}
                testId='authentication-dropdown'
                disabled={this.state.configurationIsSavedToDatabase}>
                {this.state.selectedProvider ? this.state.getProviderDisplayName(this.state.selectedProvider) : Messages.siteAuthenticationSelectType()}
              </SingleSelectDropdown>
            </div>
            : null }
          {this.state.selectedProvider ?
            <div>
              <div style={textFieldContainer}>
                {Messages.siteAuthenticationNameDesc()}
              </div>
              <TextFieldWidget
                onChange={(evt) => this.setIdpConfigurationName(evt.target.value)}
                kind='line'
                label={Messages.siteAuthenticationName()}
                style={textFieldContainer}
                testId='authentication-name-field-text'
                defaultValue={ this.state.selectedProvider?.idpConfigurationName && !this.props.isNewConfigPage ? this.state.selectedProvider?.idpConfigurationName : ''}
                disabled={ this.state.selectedProvider?.id === DEFAULT_IDP_CONFIG_ID_OIDC || this.state.selectedProvider?.id === DEFAULT_IDP_CONFIG_ID_SAML}
              />
                {this.isIdpConfigurationNameDuplicate(this.state.idpConfigurationName) && !this.state.configurationIsSavedToDatabase ?
                    <div style={samlProviderGroupSettings}>
                        <CompactInlineAlertWidget type={InlineAlertType.Info} testId="" style={samlProviderAlertText}>
                            {<div>{Messages.siteAuthenticationConfigNameError()}</div>}
                        </CompactInlineAlertWidget>
                    </div>
                : null}
                {!this.isValidIdpConfigurationName(this.state.idpConfigurationName) ?
                    <div style={samlProviderGroupSettings}>
                    <CompactInlineAlertWidget type={InlineAlertType.Info} testId="" style={samlProviderAlertText}>
                        {<div>{Messages.siteAuthenticationConfigInvalidChars()}</div>}
                    </CompactInlineAlertWidget>
                    </div>
                : null}
                {this.isIdpConfigurationNameExceedingMaxLength(this.state.idpConfigurationName) ?
                <div style={samlProviderGroupSettings}>
                  <CompactInlineAlertWidget type={InlineAlertType.Info} testId="" style={samlProviderAlertText}>
                    {<div>{Messages.siteAuthenticationConfigMaxLengthExceeded()}</div>}
                  </CompactInlineAlertWidget>
                </div>
                : null}
            </div>
            : null}
          {this.state.samlProviderProps.provider !== undefined && isSAMLSelected
            ?
            <div style={textFieldContainer}>
              <SAMLAuthenticationProvider { ...this.state.samlProviderProps }
                                          samlMetaDataFile={ this.state.samlMetadataFile }
                                          changesArePresent={ this.changesArePresent() } />
              {(this.isCancelDisabled() && this.isSaveDisabled() && this.state.configurationIsSavedToDatabase) ?
                <div style={{marginTop: '15px'}}>
                  <div>{this.renderTestConnectionButton()}</div>
                  <div>{this.renderStep7AdminLockout()}</div>
                </div>
                : null}
            </div>
            : null}
          {isGenericOIDC ?
            <div style={textFieldContainer}>
              <OIDCAuthenticationProvider {...this.state.oidcProviderProps} />
              {this.state.selectedProvider.isEnabled && this.state.configurationIsSavedToDatabase ?
                <div>{this.renderTestConnectionButton()}</div>
                : null}
            </div>
            : null}
          {this.state.selectedProvider?.type === AuthenticationProviderType.oidc && (this.state.selectedProvider as IOIDCAuthenticationProvider)?.alias === 'Salesforce' ?
            <div>
              <TextFieldWidget
                kind='line'
                label={Messages.salesforceMyDomain()}
                onChange={(evt) => this.updateOIDCSalesforceDomain((evt.target.value))}
                onClear={() => this.updateOIDCSalesforceDomain(SALESFORCE_DOMAIN_PLACEHOLDER)}
                required={false}
                placeholder={SALESFORCE_DOMAIN_PLACEHOLDER}
                style={textFieldContainer}
                testId='salesforce-domain-field-text'
                defaultValue={this.state.oidcProvider?.salesforceDomain || SALESFORCE_DOMAIN_PLACEHOLDER}
              />
            </div>
            : null}
        </div>
        <div className="tb-settings-control">
            <span className={UserSettingsStyle.cancelButton}>
                <ButtonWidget
                    type="reset"
                    buttonType={ButtonType.Outline}
                    onClick={this.cancelOnClick.bind(this)}
                    disabled={this.isCancelDisabled()}
                    testId={'cancel-changes-button'}
                >
                    {Messages.cancel()}
                </ButtonWidget>
            </span>
            <span>
                <ButtonWidget
                    type="submit"
                    buttonType={ButtonType.Primary}
                    onClick={this.saveChangesOnClick.bind(this)}
                    disabled={this.isSaveDisabled()}
                    testId={'save-changes-button'}
                >
                    {Messages.saveChanges()}
                </ButtonWidget>
            </span>
        </div>
      </div>
    </>;
  }

  private renderStep7AdminLockout(): JSX.Element {
    return (
      <div>
        <div style={samlProviderTextTitleStyle}>
          <span>{Messages.siteAuthenticationStep7()}</span>
        </div>
        <div>
          <div style={{...samlProviderGroupContent, ...samlProviderTextDescriptionStyle}}>
            {Messages.siteAuthenticationTroubleshootActions2()}
          </div>
          <ul style={ {paddingLeft: '20px'} }>
            <li style={samlProviderTextDescriptionStyle}>
              <span style={samlProviderMatchAttributesTitleStyle}>{Messages.siteAuthenticationTroubleshootAssertionMatchingTitle()}</span>
              &nbsp;
              <span>{Messages.siteAuthenticationTroubleshootAssertionMatchingDescription()}</span>
            </li>
            <div style={{paddingTop: '16px', paddingBottom: '16px'}}>
              <ButtonWidget
                buttonType={ButtonType.Outline}
                onClick={(evt) => this.downloadLog(evt)}
                testId="download-troubleshooting-log-button">
                {Messages.siteAuthenticationDownloadLog()}
              </ButtonWidget>
            </div>
            <li style={samlProviderTextDescriptionStyle}>
              <span style={samlProviderMatchAttributesTitleStyle}>{Messages.siteAuthenticationTroubleshootOption1Title()}</span>
              &nbsp;
              <span>{Messages.siteAuthenticationTroubleshootOption1Description()}</span>
            </li>
            <li style={samlProviderTextDescriptionStyle}>
              <span style={samlProviderMatchAttributesTitleStyle}>{Messages.siteAuthenticationTroubleshootOption2Title()}</span>
              &nbsp;
              <span>{Messages.siteAuthenticationTroubleshootOption2Description()}</span>
            </li>
          </ul>
        </div>
      </div>
    );
  }

  private renderTestConnectionButton(): JSX.Element {
    return(
      <div>
        <div style={samlProviderGroupSettings}>
          <CompactInlineAlertWidget type={InlineAlertType.Info} testId='' style={samlProviderAlertText}>
            {<div>{Messages.siteAuthenticationTestConnectionRecommendation()}</div>}
          </CompactInlineAlertWidget>
        </div>
        <div style={samlProviderGroupSettings}>
          <div style={samlProviderTextTitleStyle}>
            {this.isGenericOIDCSelected() ? <span style={samlProviderGroupNumber}>6.</span> : <span style={samlProviderGroupNumber}>7.</span>}
            &nbsp;
            <span>{Messages.siteAuthenticationTestLoginConfigurationRecommended()}</span>
          </div>
          <div>
            <div style={samlProviderGroupContent}>
              <div style={samlProviderTextDescriptionStyle}>
                {Messages.siteAuthenticationTestConfigurationDesc()}
              </div>
            </div>
            <div style={samlProviderGroupSettings}>
              <ButtonWidget
                className="tb-float-left"
                buttonType={ButtonType.Outline}
                onClick={this.test.bind(this)}
                testId="test-connection-button">
                {Messages.siteAuthenticationTestLoginConfiguration()}
              </ButtonWidget>

              {
                this.state.userAttributes ?
                  <div className="tb-enabled-color">
                    {Messages.testConnectionSuccess()}
                  </div>
                  : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  public render(): JSX.Element {
    if (this.state.hasError) {
      return <RouterError errorType={'default'} urls={undefined} />;
    }
    return (
      <div className="tb-scroll">
        <div style={newConfigurationContainer}>
          {this.renderAuthenticationTypes()}
        </div>
      </div>
    );
  }
}